import React, { useState, useRef, useEffect } from 'react';
import { Card, Button, Alert, Tag, Space, Modal, Typography, Upload, message, Divider } from 'antd';
import {
  SyncOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  CloudDownloadOutlined,
  DownloadOutlined,
  UploadOutlined,
  DatabaseOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import AuthService from '../utils/auth';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/zh-cn';

dayjs.extend(relativeTime);
dayjs.locale('zh-cn');

const { Text } = Typography;
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3060/api';

const SYNC_TABLES = [
  'tours', 'tourists', 'salespersons', 'destinations', 'hotels',
  'flight_batches', 'airlines', 'addon_products', 'tourist_hotels',
  'tourist_addons', 'tourist_documents', 'tour_notices', 'notice_templates',
  'notice_attachments', 'addon_notice_templates', 'addon_notice_attachments',
  'tour_disabled_addons',
];

const DataSync = () => {
  const [syncing, setSyncing] = useState(false);
  const [logs, setLogs] = useState('');
  const [status, setStatus] = useState(null); // null | 'success' | 'error'
  const [lastRun, setLastRun] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const logRef = useRef(null);
  const eventSourceRef = useRef(null);

  // 查询同步状态
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const res = await axios.get(`${API_BASE}/sync/status`, {
          headers: AuthService.getAuthHeaders(),
        });
        if (res.data.success) {
          const { running, lastRun: lr, lastResult } = res.data.data;
          setSyncing(running);
          setLastRun(lr);
          if (lastResult && !running) setStatus(lastResult);
        }
      } catch {
        // ignore
      }
    };
    fetchStatus();
  }, []);

  // 自动滚动到底部
  useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [logs]);

  const handleSync = () => {
    Modal.confirm({
      title: '确认同步',
      icon: <ExclamationCircleOutlined />,
      content: '将从线上数据库同步 17 张核心业务表到本地，本地数据会被覆盖。确定继续？',
      okText: '开始同步',
      cancelText: '取消',
      onOk: startSync,
    });
  };

  const startSync = () => {
    setSyncing(true);
    setLogs('');
    setStatus(null);

    const token = AuthService.getToken();
    const url = `${API_BASE}/sync/start`;

    // 使用 fetch + ReadableStream 读取 SSE
    const controller = new AbortController();
    eventSourceRef.current = controller;

    fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      signal: controller.signal,
    })
      .then((response) => {
        const reader = response.body.getReader();
        const decoder = new TextDecoder();

        const read = () => {
          reader.read().then(({ done, value }) => {
            if (done) {
              setSyncing(false);
              return;
            }
            const text = decoder.decode(value, { stream: true });
            // 解析 SSE data 行
            const lines = text.split('\n');
            for (const line of lines) {
              if (line.startsWith('data: ')) {
                try {
                  const payload = JSON.parse(line.slice(6));
                  if (payload.type === 'log') {
                    setLogs((prev) => prev + payload.data);
                  } else if (payload.type === 'status') {
                    setLogs((prev) => prev + payload.data + '\n');
                  } else if (payload.type === 'done') {
                    setStatus(payload.data.success ? 'success' : 'error');
                    setSyncing(false);
                    setLastRun(new Date().toISOString());
                  } else if (payload.type === 'error') {
                    setLogs((prev) => prev + '错误: ' + payload.data + '\n');
                    setStatus('error');
                    setSyncing(false);
                  }
                } catch {
                  // ignore parse errors
                }
              }
            }
            read();
          });
        };
        read();
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
          setLogs((prev) => prev + '连接失败: ' + err.message + '\n');
          setStatus('error');
        }
        setSyncing(false);
      });
  };

  const handleCancel = () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.abort();
      eventSourceRef.current = null;
      setSyncing(false);
      setLogs((prev) => prev + '\n--- 同步已取消 ---\n');
    }
  };

  // 导出数据库
  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API_BASE}/sync/export`, {
        headers: AuthService.getAuthHeaders(),
        responseType: 'blob',
      });
      // 从 Content-Disposition 取文件名
      const disposition = res.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?(.+?)"?$/);
      const filename = match ? match[1] : `passport_ocr_export.sql`;

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      message.success('数据导出成功，文件已下载');
    } catch (err) {
      message.error('导出失败: ' + (err.response?.data?.error || err.message));
    } finally {
      setExporting(false);
    }
  };

  // 导入数据库
  const handleImport = (file) => {
    Modal.confirm({
      title: '确认导入',
      icon: <ExclamationCircleOutlined />,
      content: '导入将覆盖本地数据库中对应的表数据，确定继续？',
      okText: '确认导入',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: async () => {
        setImporting(true);
        try {
          const formData = new FormData();
          formData.append('file', file);
          const res = await axios.post(`${API_BASE}/sync/import`, formData, {
            headers: {
              ...AuthService.getAuthHeaders(),
              'Content-Type': 'multipart/form-data',
            },
          });
          if (res.data.success) {
            message.success('数据导入成功');
          }
        } catch (err) {
          message.error('导入失败: ' + (err.response?.data?.error || err.message));
        } finally {
          setImporting(false);
        }
      },
    });
    return false; // 阻止自动上传
  };

  return (
    <div>
      <Card
        title={
          <Space>
            <DatabaseOutlined />
            <span>数据同步</span>
          </Space>
        }
        extra={
          <Space>
            {lastRun && (
              <Text type="secondary" style={{ fontSize: 13 }}>
                上次同步: {dayjs(lastRun).fromNow()}
              </Text>
            )}
            {status === 'success' && <Tag color="success" icon={<CheckCircleOutlined />}>成功</Tag>}
            {status === 'error' && <Tag color="error" icon={<CloseCircleOutlined />}>失败</Tag>}
          </Space>
        }
      >
        <Alert
          message="线上数据导出与本地导入"
          description={
            <div>
              <div style={{ marginBottom: 8 }}>
                在线上环境点击「导出数据」下载 SQL 文件，然后在本地环境点击「导入数据」上传该文件，即可将线上数据同步到本地。
              </div>
              <div style={{ marginBottom: 8 }}>
                涉及 {SYNC_TABLES.length} 张表：
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {SYNC_TABLES.map((t) => (
                  <Tag key={t} style={{ marginBottom: 4 }}>{t}</Tag>
                ))}
              </div>
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Space style={{ marginBottom: 16 }} wrap>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleExport}
            loading={exporting}
            size="large"
          >
            导出数据
          </Button>
          <Upload
            accept=".sql"
            showUploadList={false}
            beforeUpload={handleImport}
          >
            <Button
              icon={<UploadOutlined />}
              loading={importing}
              size="large"
            >
              导入数据
            </Button>
          </Upload>
        </Space>

      </Card>
    </div>
  );
};

export default DataSync;
